import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { DEFAULT_LOCALE } from '@/shared/config/schema'
import { useLanguage } from './hooks'
import { useLocaleStore } from './locale-store'
import { TRANSLATIONS_QUERY_SCOPE, useTranslations } from './use-translations'

/**
 * Loads the catalogue of the chosen language, keeps `<html lang>` in sync with the language on
 * screen and refetches server data after a language switch. Renders nothing.
 */
export function LocaleSync() {
  const queryClient = useQueryClient()
  useTranslations()
  const { language, servedLanguage } = useLanguage()
  const hasCatalogue = useLocaleStore((state) => Object.keys(state.resources).length > 0)
  // Without a served catalogue the texts are the bundled English, whatever was chosen.
  const shown = hasCatalogue && servedLanguage !== null ? servedLanguage : DEFAULT_LOCALE

  useEffect(() => {
    document.documentElement.lang = shown
  }, [shown])

  // Server texts are localized from the request's `X-Language`, and query keys carry no
  // language: without this the previous language's responses would stay on screen. Translations
  // are the exception: their key has the language, so each catalogue stays cached.
  const previous = useRef(language)
  useEffect(() => {
    if (previous.current === language) return
    previous.current = language
    // A query that marks itself `localeIndependent` (a file of the object storage) keeps its data;
    // a failed one is read again, so that the backend's message comes in the new language.
    void queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] !== TRANSLATIONS_QUERY_SCOPE &&
        !(query.meta?.localeIndependent === true && query.state.status === 'success'),
    })
  }, [language, queryClient])

  return null
}
